if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      /**
       * variant-selects uses display:contents (see product-variant-picker.liquid) so its fieldsets
       * participate in the parent flex layout. In some browsers, .children on the custom element
       * is empty or unreliable — we must not depend on it for option order / values.
       */
      static getVariantPickerOptionGroups(variantSelects) {
        if (!variantSelects) return [];
        const isGroup = (el) =>
          el?.matches?.('fieldset.product-form__input, div.product-form__input');

        const direct = Array.from(variantSelects.children).filter(isGroup);
        if (direct.length) return direct;

        const scoped = variantSelects.querySelectorAll(
          ':scope > fieldset.product-form__input, :scope > div.product-form__input'
        );
        if (scoped.length) return Array.from(scoped);

        const flat = Array.from(
          variantSelects.querySelectorAll('fieldset.product-form__input, div.product-form__input')
        );
        const byParent = flat.filter((el) => el.parentElement === variantSelects);
        if (byParent.length) return byParent;

        return flat;
      }
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      /** When set, handleUpdateProductInfo uses page JSON instead of server-rendered variant-selects (avoids snap-back). */
      _pendingClientVariantId = undefined;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        queueMicrotask(() => {
          const needsPurchasableFix = this.maybeRedirectToFirstPurchasableVariant();
          if (!needsPurchasableFix) {
            const vid =
              this.querySelector('form[is="product-form"] input[name="id"]')?.value ||
              this.querySelector('input.product-variant-id[name="id"]')?.value;
            let variant = vid != null ? this.getVariantFromJsonById(vid) : null;
            if (!variant) {
              variant = this.getSelectedVariant(this);
              if (variant) variant = this.enrichVariantForUi(variant);
            }
            this.currentVariant = variant;
            if (this.currentVariant) {
              this.updateMedia(this.currentVariant, { instant: true });
              this.syncPriceBlockFromClientVariant(this.currentVariant);
              this.syncVariantVendorBlockFromClientVariant(this.currentVariant);
              const cannotPurchase =
                !this.currentVariant.available ||
                this.currentVariant.price == null ||
                this.currentVariant.price <= 0;
              this.toggleAddButton(
                cannotPurchase,
                cannotPurchase ? FoxTheme.variantStrings.soldOut : undefined
              );
            }
          }
          this.variantSelectors?.syncOptionAvailabilityFromVariants?.();
        });
      }

      /**
       * When the default variant is $0, refetch with ?variant=<cheapest non-zero-price id>.
       * Prefer min price among available+priced; else min price among priced only (matches Liquid).
       */
      maybeRedirectToFirstPurchasableVariant() {
        if (this.dataset.purchasableFixDone === 'true') return false;
        const variantsEl = this.querySelector('script[data-product-variants-json]');
        if (!variantsEl) return false;
        let all;
        try {
          all = JSON.parse(variantsEl.textContent || '[]');
        } catch (e) {
          return false;
        }
        let current = this.getSelectedVariant(this);
        if (!current && all.length) {
          const idInput =
            this.querySelector('input.product-variant-id[name="id"]') ||
            this.querySelector('form[is="product-form"] input[name="id"]');
          const vid = idInput && idInput.value;
          if (vid) current = all.find((v) => String(v.id) === String(vid));
        }
        if (!current || current.price > 0) return false;
        const minByPrice = (variants) =>
          variants.reduce((best, v) => (!best || v.price < best.price ? v : best), null);
        const pricedAvailable = all.filter((v) => v.available && v.price > 0);
        const priced = all.filter((v) => v.price > 0);
        const next = minByPrice(pricedAvailable) || minByPrice(priced);
        if (!next || next.id === current.id) return false;
        this.dataset.purchasableFixDone = 'true';
        this._pendingClientVariantId = next.id;
        const productUrl = this.dataset.url;
        const viewMode = this.dataset.viewMode || 'main-product';
        this.renderProductInfo({
          requestUrl: `${productUrl}?variant=${next.id}&section_id=${this.sectionId}`,
          targetId: this.id,
          callback: this.handleUpdateProductInfo(productUrl, viewMode),
        });
        return true;
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this._pendingClientVariantId = this.resolveVariantIdFromDomSelection(event.target);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
            event.target
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }

          const pendingId = this._pendingClientVariantId;
          this._pendingClientVariantId = undefined;

          let variant =
            pendingId != null ? this.getVariantFromJsonById(pendingId) : this.getSelectedVariant(html);
          if (!variant && pendingId != null) {
            variant = this.getSelectedVariant(html);
          }

          this.pickupAvailability?.update(variant);
          // Do not replace <variant-selects> from the section response — it resets swatches/buttons
          // to server state and causes snap-back. Price/blocks still update from fetched HTML below.
          this.syncSelectedVariantScript(variant);

          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('VariantVendor');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          // Section HTML can still reflect a stale variant; align price + ATC with client JSON.
          this.syncPriceBlockFromClientVariant(variant);
          this.syncVariantVendorBlockFromClientVariant(variant);
          this.variantSelectors?.syncOptionAvailabilityFromVariants?.();
          const cannotPurchase = !variant.available || variant.price == null || variant.price <= 0;
          this.toggleAddButton(
            cannotPurchase,
            cannotPurchase ? FoxTheme.variantStrings.soldOut : undefined
          );

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          this.currentVariant = variant;

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      /**
       * Resolves variant id from current picker + variants JSON (option1/2/3 strings).
       * Prefer this over option_values for section fetches — option value ids are easy to get wrong;
       * ?variant= is what Shopify uses everywhere else (and matches maybeRedirectToFirstPurchasableVariant).
       */
      resolveVariantIdFromDomSelection(changedInput) {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return null;

        const groups = ProductInfo.getVariantPickerOptionGroups(variantSelects);
        if (!groups.length) return null;

        const selected = [];
        for (const group of groups) {
          const fromChanged =
            changedInput &&
            group.contains(changedInput) &&
            (changedInput.matches?.('input[type="radio"], input[type="checkbox"], .swatch-input__input') ||
              changedInput.tagName === 'SELECT');

          if (fromChanged && changedInput.tagName === 'SELECT') {
            const opt = changedInput.selectedOptions[0];
            if (opt) selected.push(String(opt.value).trim());
            continue;
          }
          if (
            fromChanged &&
            changedInput.matches?.('input[type="radio"], input[type="checkbox"], .swatch-input__input')
          ) {
            selected.push(String(changedInput.value).trim());
            continue;
          }

          const select = group.querySelector('select');
          if (select) {
            const opt = select.selectedOptions[0];
            if (opt) selected.push(String(opt.value).trim());
            continue;
          }
          const input = group.querySelector(
            'input[type="radio"]:checked, input[type="checkbox"]:checked, .swatch-input__input:checked'
          );
          if (input) selected.push(String(input.value).trim());
        }
        if (!selected.length) return null;

        const variantsEl = this.querySelector('script[data-product-variants-json]');
        if (!variantsEl) return null;
        let variants;
        try {
          variants = JSON.parse(variantsEl.textContent || '[]');
        } catch (e) {
          return null;
        }

        const norm = (s) => (s == null || s === '' ? '' : String(s).trim());
        const collapse = (s) => norm(s).replace(/\s+/g, ' ');

        let found = variants.find((v) => {
          if (selected.length >= 1 && norm(v.option1) !== selected[0]) return false;
          if (selected.length >= 2 && norm(v.option2) !== selected[1]) return false;
          if (selected.length >= 3 && norm(v.option3) !== selected[2]) return false;
          return true;
        });

        if (!found && selected.length) {
          const joined = selected.map((s) => collapse(s)).join(' / ');
          found = variants.find((v) => collapse(v.title) === joined);
        }

        return found ? found.id : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, changedInput) {
        const params = [];

        if (!shouldFetchFullPage) {
          params.push(`section_id=${this.sectionId}`);
        }

        const variantId = this.resolveVariantIdFromDomSelection(changedInput);
        if (variantId != null) {
          params.push(`variant=${variantId}`);
        } else if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return this.appendQueryString(url, params);
      }

      appendQueryString(url, params) {
        if (!params.length) return url;
        const qs = params.join('&');
        const sep = url.includes('?') ? '&' : '?';
        return `${url}${sep}${qs}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getVariantFromJsonById(id) {
        if (id == null) return null;
        const variantsEl = this.querySelector('script[data-product-variants-json]');
        if (!variantsEl) return null;
        try {
          const all = JSON.parse(variantsEl.textContent || '[]');
          const raw = all.find((v) => String(v.id) === String(id));
          return this.enrichVariantForUi(raw);
        } catch (e) {
          return null;
        }
      }

      enrichVariantForUi(v) {
        if (!v) return null;
        const out = { ...v };
        if (!out.options || !Array.isArray(out.options) || !out.options.length) {
          out.options = [out.option1, out.option2, out.option3].filter(
            (o) => o != null && String(o).length > 0
          );
        }
        return out;
      }

      syncSelectedVariantScript(variant) {
        if (!variant) return;
        const el = this.querySelector('variant-selects [data-selected-variant]');
        if (!el) return;
        try {
          el.textContent = JSON.stringify(variant);
        } catch (e) {
          /* ignore */
        }
      }

      /**
       * After copying price HTML from the section response, override amounts/classes from
       * data-product-variants-json so $0 / unavailable combos don't show a stale price (e.g. $1).
       */
      syncPriceBlockFromClientVariant(variant) {
        const wrap = document.getElementById(`price-${this.dataset.section}`);
        if (!wrap || !variant) return;

        const raw = variant.price;
        const parsed = raw == null || raw === '' ? NaN : Number(raw);
        const cents = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;

        wrap.classList.remove('hidden');

        const compareAt = variant.compare_at_price;
        const available = variant.available;

        const moneyFormat = FoxTheme.settings?.moneyFormat;
        const formatMoney =
          typeof FoxTheme !== 'undefined' && FoxTheme.Currency?.formatMoney
            ? (c) => FoxTheme.Currency.formatMoney(c, moneyFormat)
            : (c) => (Number(c) / 100).toFixed(2);

        const money = formatMoney(cents);
        const root = wrap.querySelector('.f-price');
        if (!root) return;

        root.classList.toggle('f-price--sold-out', !available);
        const onSale = compareAt != null && compareAt > cents;
        root.classList.toggle('f-price--on-sale', !!(onSale && available));

        const regularSpan = root.querySelector('.f-price__regular .f-price-item--regular:last-of-type');
        if (regularSpan) regularSpan.textContent = money;

        const salePriceSpan = root.querySelector('.f-price__sale .f-price-item--sale');
        if (salePriceSpan) salePriceSpan.textContent = money;

        const compareS = root.querySelector('.f-price__sale .f-price-item--regular s');
        if (compareS && onSale) {
          compareS.textContent = formatMoney(compareAt);
        }
      }

      /**
       * Variant metafields are not in data-product-variants-json. When the selection is not
       * purchasable, show a fixed vendor line; when purchasable, keep Liquid/section HTML.
       */
      syncVariantVendorBlockFromClientVariant(variant) {
        const el = document.getElementById(`VariantVendor-${this.dataset.section}`);
        if (!el || !variant) return;
        const purchasable = variant.available && variant.price != null && Number(variant.price) > 0;
        if (!purchasable) {
          el.textContent = 'By GetGreenr Singapore';
        }
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant, { instant = false } = {}) {
        const productMedia =
          this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`) ||
          this.querySelector('media-gallery');

        if (!productMedia) return;

        const mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
        if (!mediaId) return;

        const setActive = () => {
          if (typeof productMedia.init === 'function') {
            productMedia.init();
          }

          if (typeof productMedia.setActiveMedia !== 'function') return false;

          // media-gallery.js expects a variant-shaped object, not a bare media id
          productMedia.setActiveMedia({ featured_media: { id: mediaId } }, instant ? 0 : undefined);

          // Optional compatibility event for some gallery implementations
          productMedia.dispatchEvent(
            new CustomEvent('media:select', {
              bubbles: true,
              detail: { mediaId, variant },
            })
          );

          return true;
        };

        if (setActive()) return;

        clearInterval(this.mediaUpdateTimer);
        this.mediaUpdateTimer = setInterval(() => {
          if (setActive()) {
            clearInterval(this.mediaUpdateTimer);
            this.mediaUpdateTimer = null;
          }
        }, 100);

        setTimeout(() => {
          clearInterval(this.mediaUpdateTimer);
          this.mediaUpdateTimer = null;
        }, 2500);
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] .btn__text');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
